// ElevenLabs text-to-speech models and the routing rules every TTS surface
// shares (/tts, talking videos, the Video Agent, Marky's speech tool). Pure
// (constants only) so the /tts page can use the same rules as the server.
import { ELEVEN_V4_READY_VOICE_IDS } from './elevenLabsV4Ready';

// Facts below were read from GET /v1/models on our account and probed live
// on 2026-09-29:
// - Eleven v4 / v4 Turbo work on the plain REST text-to-speech endpoint, but
//   ElevenLabs' TTS websockets reject them (only the text-to-dialogue API
//   streams them).
// - v4 has no Speed, Style or speaker-boost setting: the API accepts those
//   fields and ignores them (a 1.2x request came back at normal pace).
// - `language_code`, previous_text/next_text and previous_request_ids work
//   on v4. mp3, wav and opus outputs all work.
// - v4 renders slowly: ~22 ms per character (2,000 chars took 44 s), v4
//   Turbo ~5 ms per character. That's why long texts are split into chunks
//   rendered in parallel (see @/utils/elevenLabsTts).
// - Our account charges v4 1 credit/char and v4 Turbo 0.5 at list price (a
//   launch promo made both ~72% cheaper until 2026-10-12). User billing is
//   unchanged: 1 character per character, 0.5 when the user picks Turbo.

export const ELEVEN_V4 = 'eleven_v4';
export const ELEVEN_V4_TURBO = 'eleven_v4_turbo';
export const ELEVEN_V3 = 'eleven_v3';
export const ELEVEN_MULTILINGUAL_V2 = 'eleven_multilingual_v2';
export const ELEVEN_FLASH_V2_5 = 'eleven_flash_v2_5';
// Deprecated by ElevenLabs in favour of Flash v2.5 (identical output, lower
// latency). Kept only so old pinned values still resolve.
export const ELEVEN_TURBO_V2_5 = 'eleven_turbo_v2_5';

// The audio_voices.model value behind the "Classic" choice on /tts: the
// user's own clone keeps the routing it had before Eleven v4 (see
// keepsPreV4Model). Not a fixed model: Turbo still gives such a voice Flash
// v2.5 at half price.
export const CLASSIC_VOICE_MODEL = ELEVEN_MULTILINGUAL_V2;

/**
 * True when the user may switch their own voice between Latest (Eleven v4)
 * and Classic: ElevenLabs instant clones only. Designed voices are pinned to
 * v3, which their previews were made with, and professional clones run on
 * MiniMax.
 */
export const canChooseVoiceModel = (model?: string | null) =>
  !model || model === CLASSIC_VOICE_MODEL;

export interface ElevenLabsTtsModelSpec {
  // Hard per-request limit from GET /v1/models.
  maxCharactersPerRequest: number;
  // ElevenLabs documents `language_code` as unsupported on Multilingual v2.
  supportsLanguageCode: boolean;
  // Interprets inline cues like [laughs]; other models read them aloud.
  supportsAudioTags: boolean;
  // Honours voice_settings.speed.
  supportsSpeed: boolean;
  // The half-price tier. The user discount still requires choosing Turbo.
  turboTier: boolean;
}

export const ELEVENLABS_TTS_MODELS: Record<string, ElevenLabsTtsModelSpec> = {
  [ELEVEN_V4]: {
    maxCharactersPerRequest: 10000,
    supportsLanguageCode: true,
    supportsAudioTags: true,
    supportsSpeed: false,
    turboTier: false,
  },
  [ELEVEN_V4_TURBO]: {
    maxCharactersPerRequest: 10000,
    supportsLanguageCode: true,
    supportsAudioTags: true,
    supportsSpeed: false,
    turboTier: true,
  },
  [ELEVEN_V3]: {
    maxCharactersPerRequest: 5000,
    supportsLanguageCode: true,
    supportsAudioTags: true,
    supportsSpeed: true,
    turboTier: false,
  },
  [ELEVEN_MULTILINGUAL_V2]: {
    maxCharactersPerRequest: 10000,
    supportsLanguageCode: false,
    supportsAudioTags: false,
    supportsSpeed: true,
    turboTier: false,
  },
  [ELEVEN_FLASH_V2_5]: {
    maxCharactersPerRequest: 40000,
    supportsLanguageCode: true,
    supportsAudioTags: false,
    supportsSpeed: true,
    turboTier: true,
  },
  [ELEVEN_TURBO_V2_5]: {
    maxCharactersPerRequest: 40000,
    supportsLanguageCode: true,
    supportsAudioTags: false,
    supportsSpeed: true,
    turboTier: true,
  },
};

export const getElevenLabsTtsModelSpec = (
  modelId: string,
): ElevenLabsTtsModelSpec =>
  ELEVENLABS_TTS_MODELS[modelId] || ELEVENLABS_TTS_MODELS[ELEVEN_V4];

// Longest text rendered inside one request/response cycle. Cloudflare cuts
// requests at ~100 s, and a 10,000-char v4 text split into parallel chunks
// finishes in ~45 s.
export const TTS_SYNC_MAX_CHARACTERS = 10000;
// Callers that chose Turbo could always send up to 40,000 characters in one
// request (API, mobile app, video editor). v4 Turbo keeps that under ~40 s.
export const TTS_SYNC_TURBO_MAX_CHARACTERS = 40000;
// Longer texts run as a background job the /tts page polls for (~50 min of
// audio). Uncompressed audio (~5 MB per minute at 44.1 kHz) gets a lower cap,
// since every chunk and the joined file are held in memory.
export const TTS_BACKGROUND_MAX_CHARACTERS = 50000;
export const TTS_BACKGROUND_UNCOMPRESSED_MAX_CHARACTERS = 20000;

// WAV and raw PCM / µ-law / A-law outputs.
export const isUncompressedAudioFormat = (outputFormat: string) =>
  /^(wav|pcm|ulaw|alaw)/.test(outputFormat);

/**
 * Longest text one ElevenLabs generation accepts. Background jobs (only the
 * /tts page) take long texts; callers that wait for the result keep what one
 * request/response cycle can finish.
 */
export const getTtsCharacterLimit = ({
  outputFormat,
  background,
  useTurbo,
}: {
  outputFormat: string;
  background: boolean;
  useTurbo?: boolean;
}) => {
  if (background) {
    return isUncompressedAudioFormat(outputFormat)
      ? TTS_BACKGROUND_UNCOMPRESSED_MAX_CHARACTERS
      : TTS_BACKGROUND_MAX_CHARACTERS;
  }
  return useTurbo ? TTS_SYNC_TURBO_MAX_CHARACTERS : TTS_SYNC_MAX_CHARACTERS;
};

export const isDefaultSpeechSpeed = (speed: number | undefined | null) =>
  speed === undefined || speed === null || Math.abs(Number(speed) - 1) < 0.001;

/**
 * True when a voice must stay on the model it used before Eleven v4.
 *
 * Almost every catalog voice is an ElevenLabs professional voice clone, and
 * a professional clone sounds like itself only on the models it was trained
 * for. 94% of ours are not trained for v4 (2026-10-01), and on v4 they lose
 * their character: Singaporean voices came out with a neutral accent
 * (customer report). So a catalog voice keeps its old model unless it is
 * known to be fine on v4 (@/constants/elevenLabsV4Ready, regenerated by
 * scripts/sync-elevenlabs-v4-voices.mjs). The curated expressive voices ran
 * on v3 before, which has no clone training either, so v4 is a like-for-like
 * upgrade for them; users' own instant clones need no training.
 *
 * A user's own clone can also be kept on its old model (CLASSIC_VOICE_MODEL,
 * the "Classic" choice on /tts): some clones voice an established character
 * and sound more theatrical on v4 (customer report 2026-10-03, a horror
 * narrator). Such a voice keeps exactly its pre-v4 routing, Turbo included,
 * at every speed.
 */
export const keepsPreV4Model = ({
  voiceId,
  isCatalogVoice,
  isExpressiveVoice,
  pinnedModel,
}: {
  voiceId?: string | null;
  isCatalogVoice?: boolean;
  isExpressiveVoice?: boolean;
  pinnedModel?: string | null;
}) =>
  pinnedModel === CLASSIC_VOICE_MODEL ||
  (Boolean(isCatalogVoice) &&
    !isExpressiveVoice &&
    !(voiceId && ELEVEN_V4_READY_VOICE_IDS.has(voiceId)));

export interface ElevenLabsTtsRoute {
  modelId: string;
  // Only set when the model honours it.
  languageCode: string | null;
  // True when the user chose Turbo and got a half-price model.
  discounted: boolean;
}

/**
 * Picks the ElevenLabs model for one generation.
 *
 * 1. A model pinned on the voice row wins (designed voices are pinned to v3,
 *    which is what their previews were made with). CLASSIC_VOICE_MODEL means
 *    "keep the pre-v4 routing" instead (keepsPreV4Model), so Turbo still
 *    gives that voice Flash v2.5 at half price.
 * 2. Catalog voices not trained for v4 (keepsPreV4Model), and any voice at a
 *    speed other than 1.0 (v4 can't change speed), use the model they used
 *    before v4: Flash v2.5 when Turbo is chosen (so the half price the Turbo
 *    toggle promises still applies), otherwise v3 for curated expressive
 *    voices, Flash v2.5 for language-enforced voices, Multilingual v2 for the
 *    rest.
 * 3. Otherwise Eleven v4, or v4 Turbo when the user chose Turbo.
 */
export const resolveElevenLabsTtsRoute = ({
  pinnedModel,
  voiceId,
  isCatalogVoice,
  isExpressiveVoice,
  enforcedLanguage,
  useTurbo,
  speed,
}: {
  pinnedModel?: string | null;
  voiceId?: string | null;
  // A voice from @/constants/voices (not the user's own clone).
  isCatalogVoice?: boolean;
  // Curated voices from @/constants/v3Voices.
  isExpressiveVoice?: boolean;
  // From getEnforcedLanguageCode (non-English catalog voices).
  enforcedLanguage?: string | null;
  useTurbo?: boolean;
  speed?: number | null;
}): ElevenLabsTtsRoute => {
  let modelId: string;
  if (
    pinnedModel &&
    pinnedModel.startsWith('eleven_') &&
    pinnedModel !== CLASSIC_VOICE_MODEL
  ) {
    modelId = pinnedModel;
  } else if (
    !isDefaultSpeechSpeed(speed) ||
    keepsPreV4Model({ voiceId, isCatalogVoice, isExpressiveVoice, pinnedModel })
  ) {
    modelId = useTurbo
      ? ELEVEN_FLASH_V2_5
      : isExpressiveVoice
        ? ELEVEN_V3
        : enforcedLanguage
          ? ELEVEN_FLASH_V2_5
          : ELEVEN_MULTILINGUAL_V2;
  } else {
    modelId = useTurbo ? ELEVEN_V4_TURBO : ELEVEN_V4;
  }

  const spec = getElevenLabsTtsModelSpec(modelId);
  return {
    modelId,
    languageCode:
      enforcedLanguage && spec.supportsLanguageCode ? enforcedLanguage : null,
    discounted: Boolean(useTurbo) && spec.turboTier,
  };
};

/**
 * Characters the user is charged for one generation (the `count_words`
 * column of `speeches`, which holds characters).
 */
export const getSpeechCharacterCost = (
  textLength: number,
  route: Pick<ElevenLabsTtsRoute, 'discounted'>,
) => (route.discounted ? Math.ceil(textLength * 0.5) : textLength);
